import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AlertDialog from 'components/UI/AlertDialog';
import { PrimaryBtn } from 'components/UI/Buttons';
import InfoDialog from 'components/UI/InfoDialog';
import type React from 'react';
import { type FC, useEffect, useRef, useState } from 'react';
import { useEditFollowUpMutation } from 'services/api/followUpConfig.api';
import type { FollowUpConfig, FollowUpConfigDraft } from 'services/types/followUpConfig';

import FollowUpConfigFormAttributes from '../FollowUpConfigFormAttributes';

interface FollowUpConfigTableRowProps {
  followUpConfig: FollowUpConfig;
  onUpdate?: () => void;
}

/**
 * Converts minutes to a human-readable format (days, hours, minutes)
 * @param minutes - The number of minutes to convert
 * @returns A formatted string showing days, hours, and minutes in parentheses, or empty string if 60 minutes or less
 */
const formatMinutesToReadable = (minutes: number): string => {
  if (minutes <= 60) {
    return '';
  }

  const totalMinutes = minutes;
  const days = Math.floor(totalMinutes / (24 * 60));
  const remainingAfterDays = totalMinutes % (24 * 60);
  const hours = Math.floor(remainingAfterDays / 60);
  const mins = remainingAfterDays % 60;

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  }
  if (mins > 0) {
    parts.push(`${mins} min`);
  }

  return parts.length > 0 ? `(${parts.join(' ')})` : '';
};

const FollowUpConfigTableRow: FC<FollowUpConfigTableRowProps> = ({ followUpConfig, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showArchiveConfirmDialog, setShowArchiveConfirmDialog] = useState(false);

  const [followUpConfigDraft, setFollowUpConfigDraft] = useState<FollowUpConfigDraft>({
    ...followUpConfig,
    conditionsToSend: JSON.stringify(followUpConfig.conditionsToSend || {}),
    templateComponents: followUpConfig.templateComponents ? JSON.stringify(followUpConfig.templateComponents) : null,
  });

  const [editFollowUpConfig, { isError: isEditError, error: editError, isSuccess: isEditSuccess }] =
    useEditFollowUpMutation();

  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const hasHandledSuccess = useRef(false);
  const isArchived = followUpConfig.archivedAt != null;

  // Sync draft state when followUpConfig prop changes
  useEffect(() => {
    setFollowUpConfigDraft({
      ...followUpConfig,
      conditionsToSend: JSON.stringify(followUpConfig.conditionsToSend || {}),
      templateComponents: followUpConfig.templateComponents ? JSON.stringify(followUpConfig.templateComponents) : null,
    });
  }, [followUpConfig]);

  useEffect(() => {
    if (isEditError) {
      setIsError(true);
      // Extract user-friendly error message from RTK Query error
      const extractErrorMessage = (error: unknown): string => {
        if (error && typeof error === 'object') {
          // RTK Query error structure: error.data contains the message string
          if ('data' in error && typeof error.data === 'string') {
            return error.data;
          }
          // Fallback: check for message in nested structure
          if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
            return String(error.data.message);
          }
        }
        return 'Error al actualizar el follow up';
      };
      setErrorMessage(extractErrorMessage(editError));
      setIsTogglingActive(false);
    }
  }, [isEditError, editError]);

  useEffect(() => {
    if (isEditSuccess && !hasHandledSuccess.current) {
      hasHandledSuccess.current = true;
      // Only show success dialog if we're not just toggling active status
      if (!isTogglingActive) {
        setShowSuccessDialog(true);
      }
      setIsEditing(false);
      setIsTogglingActive(false);
      if (onUpdate) {
        onUpdate();
      }
    }
    // Reset the flag when mutation is no longer successful (new mutation started)
    if (!isEditSuccess) {
      hasHandledSuccess.current = false;
    }
  }, [isEditSuccess, isTogglingActive, onUpdate]);

  const handleToggleActive = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    // Prevent activating archived follow-ups
    if (isArchived && checked) {
      setIsError(true);
      setErrorMessage('Los follow-ups archivados no pueden activarse');
      return;
    }

    setIsTogglingActive(true);
    try {
      await editFollowUpConfig({
        ...followUpConfigDraft,
        id: followUpConfig.id,
        isActive: checked,
        conditionsToSend: JSON.parse(followUpConfigDraft.conditionsToSend) as unknown as string,
        templateComponents: followUpConfigDraft.templateComponents
          ? (JSON.parse(followUpConfigDraft.templateComponents) as unknown as string)
          : null,
      }).unwrap();
      // Optimistically update the draft state
      setFollowUpConfigDraft({
        ...followUpConfigDraft,
        isActive: checked,
      });
      // onUpdate will be called by the isEditSuccess useEffect
    } catch (_error) {
      setIsError(true);
      setErrorMessage('Error al cambiar el estado del follow up');
      setIsTogglingActive(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    if (!expanded) {
      setExpanded(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to original values
    setFollowUpConfigDraft({
      ...followUpConfig,
      conditionsToSend: JSON.stringify(followUpConfig.conditionsToSend || {}),
      templateComponents: followUpConfig.templateComponents ? JSON.stringify(followUpConfig.templateComponents) : null,
    });
  };

  const handleSave = async () => {
    setIsTogglingActive(false); // Ensure we show success dialog for save
    try {
      await editFollowUpConfig({
        ...followUpConfigDraft,
        id: followUpConfig.id,
        conditionsToSend: JSON.parse(followUpConfigDraft.conditionsToSend) as unknown as string,
        templateComponents: followUpConfigDraft.templateComponents
          ? (JSON.parse(followUpConfigDraft.templateComponents) as unknown as string)
          : null,
      }).unwrap();
      // onUpdate will be called by the isEditSuccess useEffect
    } catch (_error) {
      setIsError(true);
      setErrorMessage('Error al guardar el follow up');
    }
  };

  const handleArchive = async () => {
    try {
      await editFollowUpConfig({
        ...followUpConfigDraft,
        id: followUpConfig.id,
        archivedAt: new Date(),
        isActive: false, // Archive automatically sets as inactive
        conditionsToSend: JSON.parse(followUpConfigDraft.conditionsToSend) as unknown as string,
        templateComponents: followUpConfigDraft.templateComponents
          ? (JSON.parse(followUpConfigDraft.templateComponents) as unknown as string)
          : null,
      }).unwrap();
      // Optimistically update the draft state
      setFollowUpConfigDraft({
        ...followUpConfigDraft,
        archivedAt: new Date(),
        isActive: false,
      });
      if (onUpdate) {
        onUpdate();
      }
    } catch (_error) {
      setIsError(true);
      setErrorMessage('Error al archivar el follow up');
    }
  };

  const handleUnarchive = async () => {
    try {
      await editFollowUpConfig({
        ...followUpConfigDraft,
        id: followUpConfig.id,
        archivedAt: null,
        conditionsToSend: JSON.parse(followUpConfigDraft.conditionsToSend) as unknown as string,
        templateComponents: followUpConfigDraft.templateComponents
          ? (JSON.parse(followUpConfigDraft.templateComponents) as unknown as string)
          : null,
      }).unwrap();
      if (onUpdate) {
        onUpdate();
      }
    } catch (_error) {
      setIsError(true);
      setErrorMessage('Error al desarchivar el follow up');
    }
  };

  const getSwitchTooltipTitle = () => {
    if (isArchived) {
      return 'Los follow-ups archivados no pueden activarse. Desarchiva primero.';
    }
    return followUpConfigDraft.isActive ? 'Desactivar' : 'Activar';
  };

  const getRowBackgroundColor = () => {
    if (isArchived) {
      return 'rgba(158, 158, 158, 0.1)';
    }
    if (followUpConfigDraft.isActive) {
      return 'rgba(76, 175, 80, 0.05)';
    }
    return 'rgba(244, 67, 54, 0.05)';
  };

  const onArchiveClick = () => {
    setShowArchiveConfirmDialog(true);
    return undefined;
  };

  const confirmArchive = () => {
    handleArchive().catch(() => {
      // Error handling is done in handleArchive
    });
  };

  const onUnarchiveClick = () => {
    handleUnarchive().catch(() => {
      // Error handling is done in handleUnarchive
    });
    return undefined;
  };

  return (
    <>
      <AlertDialog
        openDialog={isError}
        setOpenDialog={setIsError}
        header="Error"
        msg={errorMessage}
        cancelMsg="Cerrar"
      />
      <AlertDialog
        openDialog={showArchiveConfirmDialog}
        setOpenDialog={setShowArchiveConfirmDialog}
        header="Confirmar archivo"
        msg="¿Estás seguro de que deseas archivar este follow-up? El follow-up será desactivado automáticamente."
        confirmMsg="Archivar"
        confirmAction={confirmArchive}
        cancelMsg="Cancelar"
      />
      {showSuccessDialog && (
        <InfoDialog
          header="Follow up actualizado!"
          msg="Follow up fue actualizado correctamente"
          onClose={() => {
            setShowSuccessDialog(false);
          }}
        />
      )}
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: 'unset' },
          backgroundColor: getRowBackgroundColor(),
        }}
      >
        <TableCell sx={{ width: '50px', minWidth: '50px' }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
            {expanded ? <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ width: '80px', minWidth: '80px' }}>{followUpConfig.id}</TableCell>
        <TableCell sx={{ width: '200px', minWidth: '200px' }}>
          {(() => {
            const { identifier } = followUpConfig;
            const maxLength = 35;
            const truncated = identifier.length > maxLength ? `${identifier.slice(0, maxLength)}...` : identifier;
            return (
              <Tooltip title={identifier.length > maxLength ? identifier : ''} arrow>
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {truncated}
                </Typography>
              </Tooltip>
            );
          })()}
        </TableCell>
        <TableCell sx={{ width: '150px', minWidth: '150px' }}>
          {followUpConfig.minWorkingHour} - {followUpConfig.maxWorkingHour}
        </TableCell>
        <TableCell sx={{ width: '180px', minWidth: '180px' }}>
          {followUpConfig.minMinutesWaitTime}
          {formatMinutesToReadable(followUpConfig.minMinutesWaitTime)} - {followUpConfig.maxMinutesWaitTime}
          {formatMinutesToReadable(followUpConfig.maxMinutesWaitTime)}
        </TableCell>
        <TableCell align="center" sx={{ width: '120px', minWidth: '120px' }}>
          {followUpConfig.sendOnWeekend ? 'Sí' : 'No'}
        </TableCell>
        <TableCell align="center" sx={{ width: '100px', minWidth: '100px' }}>
          <Chip
            label={followUpConfigDraft.isActive ? 'Activo' : 'Inactivo'}
            color={followUpConfigDraft.isActive ? 'success' : 'error'}
            size="small"
          />
        </TableCell>
        <TableCell sx={{ width: '150px', minWidth: '150px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
            }}
          >
            <Tooltip title={getSwitchTooltipTitle()}>
              <span>
                <Switch
                  checked={followUpConfigDraft.isActive}
                  onChange={(e, checked) => {
                    handleToggleActive(e, checked).catch(() => {
                      // Error handling is done in handleToggleActive
                    });
                  }}
                  color="success"
                  size="small"
                  disabled={isArchived}
                />
              </span>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            {isArchived ? (
              <Tooltip title="Desarchivar">
                <IconButton size="small" onClick={onUnarchiveClick}>
                  <UnarchiveIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Archivar">
                <IconButton size="small" onClick={onArchiveClick}>
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                py: 2,
                px: 2,
                backgroundColor: 'white',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              {isEditing && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                    pb: 1.5,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <PrimaryBtn
                    onClick={() => {
                      handleSave().catch(() => {
                        // Error handling is done in handleSave
                      });
                    }}
                    startIcon={<EditIcon />}
                    variant="contained"
                  >
                    Guardar
                  </PrimaryBtn>
                  <PrimaryBtn onClick={handleCancelEdit} variant="outlined">
                    Cancelar
                  </PrimaryBtn>
                </Box>
              )}
              <FollowUpConfigFormAttributes
                followUpConfigDraft={followUpConfigDraft}
                setFollowUpConfigDraft={setFollowUpConfigDraft}
                isEditing={isEditing}
                businessUnitId={followUpConfig.businessUnitId}
                hideVisibleFields
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default FollowUpConfigTableRow;
